import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import LocalHelmet from '../common/LocalHelmet'
import { motion } from 'framer-motion'
import SafeIcon from '../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import commentaryData from '../content/commentaryData.json'
import { trackPageView } from '../utils'

const RAW_BASE = 'https://raw.githubusercontent.com/richardsonstrategygroup/rsg-assets/main';
const CDN_BASE = 'https://cdn.jsdelivr.net/gh/richardsonstrategygroup/rsg-assets@main';
const toCdn = (u) => u?.replace(RAW_BASE, CDN_BASE);

// normalize any "github.com/.../blob/..." link into a raw URL
const toRaw = (u) =>
  u?.includes('github.com') && u?.includes('/blob/')
    ? u.replace('https://github.com', 'https://raw.githubusercontent.com').replace('/blob/', '/')
    : u;

const { FiArrowRight, FiClock, FiUser, FiMail, FiFilter } = FiIcons

const StrategicCommentary = () => {
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [newsletterEmail, setNewsletterEmail] = useState('')
  const [isNewsletterSubmitted, setIsNewsletterSubmitted] = useState(false)

  const { sectionInfo, featuredPosts, recentPosts, categories } = commentaryData

  useEffect(() => {
    trackPageView('/strategic-commentary')
  }, [])

  const allPosts = [...featuredPosts, ...recentPosts]
  
  const filteredPosts = selectedCategory === 'all' 
    ? allPosts 
    : allPosts.filter(post => post.category.toLowerCase() === selectedCategory)

  const handleNewsletterSubmit = (e) => {
    e.preventDefault()
    // Newsletter signup logic would go here
    console.log('Newsletter signup:', newsletterEmail)
    setIsNewsletterSubmitted(true)
    setTimeout(() => setIsNewsletterSubmitted(false), 3000)
    setNewsletterEmail('')
  }

  return (
    <>
      <LocalHelmet prioritizeSeoTags>
      <title>Strategic Commentary | Richardson Strategy Group</title>
      <meta
        name="description"
        content="Read expert commentary and analysis on strategy, measurement, AI, and growth trends from Richardson Strategy Group. Actionable insights for executives who want to stay ahead."
      />
      <link rel="canonical" href="https://richardsonstrategy.group/strategic-commentary" />
      {/* Open Graph */}
      <meta property="og:site_name" content="Richardson Strategy Group" />
      <meta property="og:type" content="article" />
      <meta
        property="og:title"
        content="Strategic Commentary | Richardson Strategy Group"
      />
      <meta
        property="og:description"
        content="Actionable strategic insights. See how top leaders use data, AI, and measurement to make real progress."
      />
      <meta property="og:url" content="https://richardsonstrategy.group/strategic-commentary" />
      <meta
        property="og:image"
        content="https://raw.githubusercontent.com/richardsonstrategygroup/rsg-assets/main/social/og-commentary.jpg"
      />
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta
        name="twitter:title"
        content="Strategic Commentary | Richardson Strategy Group"
      />
      <meta
        name="twitter:description"
        content="Expert insights, direct advice—strategy that works for leaders."
      />
      <meta
        name="twitter:image"
        content="https://raw.githubusercontent.com/richardsonstrategygroup/rsg-assets/main/social/og-commentary.jpg"
      />
      {/* JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Blog',
          name: 'Richardson Strategy Group Strategic Commentary',
          url: 'https://richardsonstrategy.group/strategic-commentary',
          description:
            'Business strategy and analytics commentary—actionable ideas for executives.',
        })}
      </script>
    </LocalHelmet>

      <div className="min-h-screen py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Header */}
          <div className="text-center mb-12 md:mb-16">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-3xl sm:text-4xl md:text-5xl font-bold text-secondary-900 mb-4 md:mb-6"
            >
              {sectionInfo.title}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-lg md:text-xl text-secondary-600 max-w-4xl mx-auto leading-relaxed mb-4"
            >
              {sectionInfo.subtitle}
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-base text-secondary-500 italic"
            >
              {sectionInfo.description}
            </motion.p>
          </div>

          {/* Featured Posts */}
          <div className="mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-secondary-900 mb-8">Featured Analysis</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {featuredPosts.map((post, index) => (
                <motion.article
                  key={post.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-white rounded-2xl shadow-sm border border-secondary-100 overflow-hidden hover:shadow-md transition-shadow"
                >
                  <div className="aspect-w-16 aspect-h-9">
                    <img
                      src={toRaw(post.image)}
                      alt={post.title}
                      className="w-full h-48 object-cover"
                      loading="lazy"
                      onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = toCdn(toRaw(post.image)); }}
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center space-x-4 text-sm text-secondary-500 mb-3">
                      <span className="bg-primary-100 text-primary-700 px-2 py-1 rounded-full text-xs font-medium">
                        {post.category}
                      </span>
                      <div className="flex items-center space-x-1">
                        <SafeIcon icon={FiClock} className="w-4 h-4" />
                        <span>{post.readTime}</span>
                      </div>
                      <span>{new Date(post.publishDate).toLocaleDateString()}</span>
                    </div>
                    <h3 className="text-xl font-bold text-secondary-900 mb-3 leading-tight">
                      <Link 
                        to={`/strategic-commentary/${post.slug}`}
                        className="hover:text-primary-600 transition-colors"
                      >
                        {post.title}
                      </Link>
                    </h3>
                    <p className="text-secondary-600 mb-4 leading-relaxed">
                      {post.summary}
                    </p>
                    <Link
                      to={`/strategic-commentary/${post.slug}`}
                      className="inline-flex items-center space-x-2 text-primary-600 font-medium hover:text-primary-700 transition-colors"
                    >
                      <span>Read Analysis</span>
                      <SafeIcon icon={FiArrowRight} className="w-4 h-4" />
                    </Link>
                  </div>
                </motion.article>
              ))}
            </div>
          </div>

          {/* Newsletter Signup */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="bg-primary-50 rounded-2xl p-6 md:p-8 mb-16 text-center"
          >
            <h3 className="text-xl md:text-2xl font-bold text-secondary-900 mb-4">
              Strategic Insights Delivered
            </h3>
            <p className="text-secondary-600 mb-6 max-w-2xl mx-auto">
              Get our latest analysis on growth strategy, measurement, and AI implementation. 
              No fluff, no spam—just insights that help you make better decisions.
            </p>
            
            {isNewsletterSubmitted ? (
              <div className="bg-green-100 text-green-800 px-4 py-3 rounded-lg inline-block">
                ✓ Thanks! We'll send you our next strategic insight.
              </div>
            ) : (
              <form onSubmit={handleNewsletterSubmit} className="max-w-md mx-auto">
                <div className="flex flex-col sm:flex-row gap-3">
                  <input
                    type="email"
                    value={newsletterEmail}
                    onChange={(e) => setNewsletterEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                    className="flex-1 px-4 py-3 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <button
                    type="submit"
                    className="bg-primary-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-primary-700 transition-colors flex items-center justify-center space-x-2"
                  >
                    <SafeIcon icon={FiMail} className="w-4 h-4" />
                    <span>Subscribe</span>
                  </button>
                </div>
                <p className="text-xs text-secondary-500 mt-2">
                  Typically 2 emails per month. Unsubscribe anytime.
                </p>
              </form>
            )}
          </motion.div>

          {/* Category Filter */}
          <div className="mb-8">
            <div className="flex items-center space-x-4 mb-6">
              <SafeIcon icon={FiFilter} className="w-5 h-5 text-secondary-600" />
              <h3 className="text-lg font-semibold text-secondary-900">Filter by Topic</h3>
            </div>
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <button
                  key={category.slug}
                  onClick={() => setSelectedCategory(category.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    selectedCategory === category.slug
                      ? 'bg-primary-600 text-white'
                      : 'bg-secondary-100 text-secondary-700 hover:bg-secondary-200'
                  }`}
                >
                  {category.name} ({category.count})
                </button>
              ))}
            </div>
          </div>

          {/* Recent Posts */}
          <div className="mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-secondary-900 mb-8">
              {selectedCategory === 'all' ? 'Latest Insights' : `${categories.find(c => c.slug === selectedCategory)?.name} Insights`}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map((post, index) => (
                <motion.article
                  key={post.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-white rounded-xl shadow-sm border border-secondary-100 overflow-hidden hover:shadow-md transition-shadow"
                >
                  <div className="aspect-w-16 aspect-h-9">
                    <img
                      src={toRaw(post.image)}
                      alt={post.title}
                      className="w-full h-40 object-cover"
                      loading="lazy"
                      onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = toCdn(toRaw(post.image)); }}
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center space-x-3 text-sm text-secondary-500 mb-3">
                      <span className="bg-secondary-100 text-secondary-700 px-2 py-1 rounded-full text-xs font-medium">
                        {post.category}
                      </span>
                      <div className="flex items-center space-x-1">
                        <SafeIcon icon={FiClock} className="w-4 h-4" />
                        <span>{post.readTime}</span>
                      </div>
                    </div>
                    <h3 className="text-lg font-bold text-secondary-900 mb-3 leading-tight">
                      <Link 
                        to={`/strategic-commentary/${post.slug}`}
                        className="hover:text-primary-600 transition-colors"
                      >
                        {post.title}
                      </Link>
                    </h3>
                    <p className="text-secondary-600 text-sm mb-4 leading-relaxed">
                      {post.summary}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-secondary-500">
                        {new Date(post.publishDate).toLocaleDateString()}
                      </span>
                      <Link
                        to={`/strategic-commentary/${post.slug}`}
                        className="text-primary-600 font-medium hover:text-primary-700 transition-colors text-sm"
                      >
                        Read more →
                      </Link>
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          </div>

          {/* CTA Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="bg-secondary-50 rounded-2xl p-6 md:p-8 text-center"
          >
            <h3 className="text-xl md:text-2xl font-bold text-secondary-900 mb-4">
              Need Strategic Guidance for Your Business?
            </h3>
            <p className="text-secondary-600 mb-6 max-w-2xl mx-auto">
              These insights come from real client work. If you're facing similar strategic challenges, 
              let's discuss how Richardson Strategy Group can help.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-primary-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-primary-700 transition-colors inline-flex items-center justify-center space-x-2"
              >
                <span>Start a Conversation</span>
                <SafeIcon icon={FiArrowRight} className="w-4 h-4" />
              </Link>
              <Link
                to="/services"
                className="border border-secondary-300 text-secondary-700 px-6 py-3 rounded-lg font-medium hover:bg-white transition-colors"
              >
                Explore Our Services
              </Link>
            </div>
          </motion.div>

        </div>
      </div>
    </>
  )
}

export default StrategicCommentary